import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '@medical/shared/contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import HospitalLogo from '@medical/shared/components/HospitalLogo';
import { useHospitalBranding } from '@medical/shared/utils/hospitalBranding';
import { detectLoginRole } from '@medical/shared/utils/accountIds';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: ${props => props.$registrationMode ? '1fr' : '1fr 1fr'};
  align-items: ${props => props.$registrationMode ? 'start' : 'center'};
  justify-items: center;
  background: ${props => props.$registrationMode ? '#0B63C5' : '#ffffff'};
  padding: ${props => props.$registrationMode ? '1rem' : '0'};
  overflow-y: auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 1rem;
  }
`;

const LoginArt = styled.div`
  align-self: stretch;
  justify-self: stretch;
  min-height: 100vh;
  background: #eef0fb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem;

  img {
    width: min(420px, 85%);
    height: auto;
    border-radius: 24px;
    mix-blend-mode: multiply;
  }

  p {
    margin: 1.5rem 0 0;
    max-width: 340px;
    text-align: center;
    color: #334155;
    line-height: 1.55;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const LoginTitle = styled.h1`
  margin: 0 0 1rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: ${props => props.$wide ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' : 'none'};
  padding: ${props => props.$wide ? '1rem 1.5rem' : '2rem'};
  width: 100%;
  max-width: ${props => props.$wide ? '900px' : '400px'};
  max-height: ${props => props.$wide ? '95vh' : 'auto'};
  overflow: ${props => props.$wide ? 'hidden' : 'visible'};
  display: flex;
  flex-direction: column;
  margin-top: ${props => props.$wide ? '0.5rem' : '0'};
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: ${props => props.$compact ? '1rem' : '2rem'};
  flex-shrink: 0;
`;

const Wordmark = styled.img`
  width: 96px;
  height: 96px;
  object-fit: contain;
  border-radius: 16px;
`;

const TabContainer = styled.div`
  display: flex;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 2rem;
`;

const Tab = styled.button`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#1f2937' : '#6b7280'};
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};
  cursor: pointer;

  &:hover {
    color: #1f2937;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordField = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const PasswordInput = styled(Input)`
  padding-right: 2.75rem;
  width: 100%;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  min-width: 3.5rem;
  padding: 0.35rem 0.5rem;
  border: none;
  background: rgba(0,0,0,0.04);
  border-radius: 6px;
  color: #4b5563;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: rgba(0,0,0,0.08);
    color: #1f2937;
  }
`;

const Button = styled.button`
  background: #0B63C5;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin-top: 0.5rem;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 0.5rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  user-select: none;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const RadioOption = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 2px solid ${props => props.selected ? '#0B63C5' : '#d1d5db'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? '#f0f4ff' : 'white'};

  &:hover {
    border-color: #0B63C5;
    background: #f0f4ff;
  }
`;

const RadioInput = styled.input`
  width: 1rem;
  height: 1rem;
  cursor: pointer;
`;

const RadioLabel = styled.label`
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  user-select: none;
  font-weight: ${props => props.selected ? '600' : '400'};
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background: white;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const OptionButton = styled.button`
  background: ${props => props.primary 
    ? '#0B63C5' 
    : 'white'};
  color: ${props => props.primary ? 'white' : '#0B63C5'};
  border: 2px solid #0B63C5;
  border-radius: 8px;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BackButton = styled.button`
  background: transparent;
  color: #6b7280;
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: color 0.2s ease;

  &:hover {
    color: #0B63C5;
  }
`;

const FrontDeskNotice = styled.div`
  margin-bottom: 1.25rem;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const PasskeyContainer = styled.div`
  margin-bottom: 1rem;
`;

const FormSection = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #0B63C5;
`;

const SectionSubtitle = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: -0.5rem 0 1rem 0;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  flex: 1;
  min-width: 120px;
`;

const ScrollableForm = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 0.5rem;
  margin-bottom: 1rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #0B63C5;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #5568d3;
  }
`;

// Admin passkey - in production, this should be stored in environment variables
const ADMIN_PASSKEY = process.env.REACT_APP_ADMIN_PASSKEY || 'ADMIN2024';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState('login'); // login only — registration is handled by front desk
  const [isLogin, setIsLogin] = useState(true);
  const [selectedRole, setSelectedRole] = useState(''); // Start with no role selected
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    contactNumber: '',
    email: '',
    phone: '',
    confirmPassword: '',
    assignedDoctor: '',
    hospitalId: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const { login, register, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Helper function to reset form data
  const resetFormData = () => ({
    identifier: '',
    password: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    contactNumber: '',
    email: '',
    phone: '',
    confirmPassword: '',
    assignedDoctor: '',
    hospitalId: '',
    rememberMe: false
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };



  const handleInitialChoice = (choice) => {
    if (choice === 'login') {
      setView('login');
      setIsLogin(true);
    } else if (choice === 'register') {
      setView('roleSelection');
      setIsLogin(false);
    }
    setError('');
  };

  const handleBackToInitial = () => {
    setView('initial');
    setError('');
    setFormData(resetFormData());
    setSelectedRole('');
  };

  const handleRoleSelection = (role) => {
    setSelectedRole(role);
    setView('register');
    setError('');
    setFormData(resetFormData());
  };

  const handleBackToRoleSelection = () => {
    setView('roleSelection');
    setError('');
    setFormData(resetFormData());
  };

  // Doctor IDs, admin IDs, emails, and phone numbers each go to the matching login
  const detectUserType = (identifier) => {
    const role = detectLoginRole(identifier);
    return role === 'front_desk' ? 'patient' : role;
  };

  const signInDoctor = async () => {
    const response = await axios.post('/api/auth/doctor/login', {
      identifier: formData.identifier.trim(),
      password: formData.password,
      rememberMe: formData.rememberMe
    });

    const { token, refreshToken, user } = response.data;

    localStorage.setItem('doctorToken', token);
    if (formData.rememberMe && refreshToken) {
      localStorage.setItem('doctorRefreshToken', refreshToken);
    }
    localStorage.setItem('doctorUser', JSON.stringify(user));

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    toast.success(t('login.success.welcomeDoctor', { name: user.firstName }));
    navigate('/doctor');
  };

  const signInPatient = async () => {
    const result = await login(formData.identifier.trim(), formData.password, formData.rememberMe);
    if (!result.success) {
      setError(result.error);
      return;
    }
    navigate('/body-diagram');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isLogin) {
      // Login
      if (!formData.identifier) {
        setError(t('login.errors.missingId'));
        return;
      }

      if (!formData.password) {
        setError(t('login.errors.missingPassword'));
        return;
      }

      setLoading(true);

      try {
        const userType = detectUserType(formData.identifier);
        console.log('Detected user type:', userType, 'for identifier:', formData.identifier);

        if (!userType) {
          setError(t('login.errors.invalidIdFormat'));
          setLoading(false);
          return;
        }

        if (userType === 'admin') {
          // Admin login
          const response = await axios.post('/api/auth/admin/login', {
            identifier: formData.identifier.trim().toUpperCase(),
            password: formData.password,
            rememberMe: formData.rememberMe
          });

          const { token, refreshToken, user } = response.data;

          // Store tokens
          localStorage.setItem('adminToken', token);
          if (formData.rememberMe && refreshToken) {
            localStorage.setItem('adminRefreshToken', refreshToken);
          }

          // Store user data
          localStorage.setItem('adminUser', JSON.stringify(user));

          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          toast.success(t('toast.adminWelcome', { name: user.firstName }));
          
          // Dispatch custom event to notify AdminProtectedRoute of login
          window.dispatchEvent(new Event('adminLogin'));
          
          // Navigate to admin dashboard
          navigate('/admin');
        } else if (userType === 'doctor') {
          await signInDoctor();
        } else if (userType === 'email') {
          try {
            await signInDoctor();
          } catch (doctorError) {
            if (doctorError.response?.status !== 401) throw doctorError;
            await signInPatient();
          }
        } else {
          await signInPatient();
        }
      } catch (error) {
        console.error('Login error:', error);
        const message = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || t('login.errors.genericLogin');
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    } else {
      // Registration
      if (!formData.password) {
        setError(t('login.errors.missingPassword'));
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError(t('login.errors.passwordMismatch'));
        return;
      }

      if (formData.password.length < 6) {
        setError(t('login.errors.passwordLength'));
        return;
      }

      // Role-based registration
      if (!formData.firstName) {
        setError(t('common.requiredField'));
        return;
      }

      if (selectedRole === 'doctor') {
        // Doctor registration
        if (!formData.email) {
          setError(t('login.errors.missingDoctorEmail'));
          return;
        }

        if (!formData.phone) {
          setError(t('login.errors.missingDoctorPhone'));
          return;
        }

        if (!formData.gender) {
          setError('Gender is required');
          return;
        }

        const userData = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName || '',
          gender: formData.gender,
          phone: formData.phone,
          role: 'doctor'
        };

        setLoading(true);
        try {
          const response = await axios.post('/api/auth/doctor/register', userData);

          const { doctorId, user } = response.data;

          // DO NOT store tokens or log user in
          // DO NOT navigate to dashboard
          // Just show success message

          toast.success(
            t('toast.doctorRegistrationSuccess', { id: doctorId }),
            { duration: 10000 }
          );

          // Clear form and switch back to initial view
          setFormData(resetFormData());
          setView('initial');
          setIsLogin(true);
        } catch (error) {
          console.error('Doctor registration error:', error);
          const message = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Registration failed';
          setError(message);
          toast.error(message);
        } finally {
          setLoading(false);
        }
      } else {
        setError('New patients are registered at the hospital front desk.');
      }
    }
  };

  // Fetch doctors list when patient registration is shown
  useEffect(() => {
    const fetchDoctors = async () => {
      if (view === 'register' && !isLogin && selectedRole === 'patient') {
        setLoadingDoctors(true);
        try {
          const response = await axios.get('/api/auth/doctors');
          if (response.data.success) {
            setDoctors(response.data.doctors || []);
          }
        } catch (error) {
          console.error('Error fetching doctors:', error);
          setError(t('login.errors.loadDoctorsFailed'));
        } finally {
          setLoadingDoctors(false);
        }
      }
    };

    fetchDoctors();
  }, [view, isLogin, selectedRole, t]);

  const switchTab = (loginMode) => {
    setIsLogin(loginMode);
    setError('');
    setSelectedRole(''); // Reset role selection
    setFormData(resetFormData());
    // Update view based on login mode
    if (loginMode) {
      setView('login');
    } else {
      setView('roleSelection');
    }
  };

  const branding = useHospitalBranding(formData.identifier);
  const isRegistrationMode = view === 'register' && selectedRole === 'patient';
  const isCompactMode = view === 'register' || view === 'roleSelection';
  const showLogo = view !== 'register' && view !== 'roleSelection';

  return (
    <LoginContainer $registrationMode={isRegistrationMode}>
      <LoginCard $wide={isRegistrationMode}>
        {showLogo && (
          <Logo $compact={isCompactMode}>
            <Wordmark $compact={isCompactMode} src="/logo-wordmark.jpg" alt="Anuvedana" />
            <HospitalLogo logoUrl={branding?.logoUrl} name={branding?.name} />
          </Logo>
        )}

        {/* Login Form */}
        {view === 'login' && (
          <>
            <LoginTitle>Patient &amp; Doctor Login</LoginTitle>
            <FrontDeskNotice>
              New patients: visit the hospital front desk to register. You will receive a Patient ID and password to sign in here.
            </FrontDeskNotice>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="identifier">{t('login.form.idLabel')}</Label>
                <Input
                  type="text"
                  id="identifier"
                  name="identifier"
                  value={formData.identifier}
                  onChange={handleInputChange}
                  placeholder={t('login.form.idPlaceholder')}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="password">{t('login.form.passwordLabel')}</Label>
                <PasswordField>
                  <PasswordInput
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t('login.form.passwordPlaceholder')}
                    required
                  />
                  <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'} title={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? '🙈 Hide' : '👁 Show'}
                  </PasswordToggle>
                </PasswordField>
              </FormGroup>

              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={(e) => setFormData(prev => ({ ...prev, rememberMe: e.target.checked }))}
                />
                <CheckboxLabel htmlFor="rememberMe">
                  {t('login.form.rememberMe')}
                </CheckboxLabel>
              </CheckboxContainer>

              {error && <ErrorMessage>{error}</ErrorMessage>}

              <Button type="submit" disabled={loading || authLoading}>
                {loading || authLoading ? t('common.loading') : t('buttons.login')}
              </Button>
            </Form>
          </>
        )}

        {/* Role Selection View */}
        {view === 'roleSelection' && (
          <>
            <BackButton onClick={handleBackToInitial}>← {t('common.back')}</BackButton>
            <Form>
              <FormGroup>
                <Label style={{ fontSize: '1.125rem', marginBottom: '1.5rem', textAlign: 'center', display: 'block' }}>
                  {t('login.register.title')}
                </Label>
                <RadioGroup>
                  <FrontDeskNotice>
                    New patients: visit the hospital front desk to register. You will receive a Patient ID and password to sign in here.
                  </FrontDeskNotice>
                  <RadioOption 
                    selected={false}
                    onClick={() => handleRoleSelection('doctor')}
                    style={{ cursor: 'pointer' }}
                  >
                    <RadioInput
                      type="radio"
                      name="role"
                      value="doctor"
                      checked={false}
                      readOnly
                    />
                    <RadioLabel>
                      {t('login.register.doctor')}
                    </RadioLabel>
                  </RadioOption>
                </RadioGroup>
              </FormGroup>
            </Form>
          </>
        )}

        {/* Registration Form */}
        {view === 'register' && selectedRole && (
          <>
            <BackButton onClick={handleBackToRoleSelection}>← {t('common.back')}</BackButton>
            <Form onSubmit={handleSubmit}>
              {selectedRole === 'doctor' && (
                <>
                  <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f0f4ff', borderRadius: '8px', border: '1px solid #0B63C5' }}>
                    <strong style={{ color: '#0B63C5' }}>Registering as: Doctor</strong>
                  </div>
                  <FormGroup>
                    <Label htmlFor="firstName">{t('login.register.firstName.label')}</Label>
                    <Input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder={t('login.register.firstName.placeholder')}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="lastName">{t('login.register.lastName.label')}</Label>
                    <Input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder={t('login.register.lastName.placeholder')}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="email">{t('login.register.email.label')}</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('login.register.email.placeholder')}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="phone">{t('login.register.phone.label')}</Label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('login.register.phone.placeholder')}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="gender">Gender *</Label>
                    <Select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">-- Select Gender --</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer-not-to-say">Prefer not to say</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="password">{t('login.register.password.label')}</Label>
                    <PasswordField>
                      <PasswordInput
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder={t('login.register.password.placeholder')}
                        required
                      />
                      <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'} title={showPassword ? 'Hide password' : 'Show password'}>
                        {showPassword ? '🙈 Hide' : '👁 Show'}
                      </PasswordToggle>
                    </PasswordField>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="confirmPassword">{t('login.register.confirmPassword.label')}</Label>
                    <PasswordField>
                      <PasswordInput
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder={t('login.register.confirmPassword.placeholder')}
                        required
                      />
                      <PasswordToggle type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'} title={showConfirmPassword ? 'Hide password' : 'Show password'}>
                        {showConfirmPassword ? '🙈 Hide' : '👁 Show'}
                      </PasswordToggle>
                    </PasswordField>
                  </FormGroup>

                  {error && <ErrorMessage>{error}</ErrorMessage>}

                  <Button type="submit" disabled={loading || authLoading}>
                    {loading || authLoading ? t('common.loading') : t('buttons.register')}
                  </Button>
                </>
              )}

              </Form>
          </>
        )}
      </LoginCard>
      {!isRegistrationMode && (
        <LoginArt>
          <img src="/login-doctor.jpg" alt="Doctor reviewing a pain map with a patient" />
          <p>Track pain, share it with your doctor, and get care that fits how you feel.</p>
        </LoginArt>
      )}
    </LoginContainer>
  );
};

export default Login;
